// The ground's host: the device tier, the terrain shader variants and the
// one material path, around the ground tree (GROUND-M, groundtree.js — the
// ONLY ground; APP_GROUNDM.md). A bake without meta.groundm is refused.
// MeshBasicMaterial everywhere (lighting is baked into the texture at bake
// time -> zero runtime lights on terrain, the cheapest possible path on
// Quest). The bake cuts bores and pave into the node meshes (groundm/
// bakectx.mjs: borecut.js cutTile), so the client draws them as shipped.
//
// TerrainManager hands update / groundDrawn / pending / the near latch to
// the GroundTree (standing is ground.js ClientGround, the server's sampler)
// and owns what every node shares: the material (_material: one program per
// variant mask, _maskFor / _gmMask), the carve / water re-classification and
// the shader warm-up keepers.
import * as THREE from 'three';
import { cutUniforms, CUT_DECL_GLSL, cutGlsl, cutBoxes, cutGen } from './worldcut.js';
import { corridorUniforms, COR_DECL_GLSL, corGlsl, corridorBoxes, corridorGen } from './corridor.js';
import { bindGround, groundDecl, groundGLSL, macroGLSL, mapGLSL, groundKey, setGroundBands, setCoarseClasses,
  waterDecl, bareWaterGLSL, waterKey, waterIn, waterGen, setWaterOn, glslMin } from './groundmat.js';
import RENDER_CFG from './renderconfig.json';
import { detectDevice } from './device.js';
import { vrsGroundOn, vrsGroundMap, vrsGroundKey } from './vrstyle/ground.js';
import { buildRings } from '../../game/lodmath.js';
import RING_TIERS from './ringtiers.json';
import { GroundTree } from './groundtree.js';
import { GmTex, VT_DECL_GLSL, VT_PRE_GLSL, VT_MAP_GLSL, VT_MAP } from './gmtex.js';
import { createFarAtlas, farAtlasPx, farDecl } from './faratlas.js';
import { GroundGen } from './groundgen.js';
import { DQ } from '../../groundm/stitch.js';
import { sec } from './sched.js';

// ------------------------------------------------------------- focus wedge
// Scope focus streaming (client/src/focus.js). ONE shared uniform object on
// every ground-material program: xy = eased anchor direction, z = wedge
// half-angle in RADIANS, w = 1 only during an optic pass. Focus owns the
// Vector4 and writes it in place; setFocusWedge swaps our reference to that
// instance once at boot (the same trick as groundmat's GU — materials
// reference the OBJECT, the renderer reads .value every draw). Until then,
// and on any build without a Focus (shaderprobe), w = 0.
const _wedge = { value: new THREE.Vector4(0, 0, 0, 0) };
// The optic zoom (focus.uZoom: zoom during an optic pass, 1 otherwise) — a
// float, so the OBJECT itself is shared: setFocusWedge runs in boot before any
// terrain material compiles, and every V_GROUND material takes this object.
let _zoomU = { value: 1 };
export function setFocusWedge(u, zoom) {
  if (u && u.value) _wedge.value = u.value;
  if (zoom && typeof zoom.value === 'number') _zoomU = zoom;
}
// ground-material fades in an optic pass: distances divide by uZoom x this
// (the 1x monocular's pixel is ~3x finer than the eye's — renderconfig.focus)
const OPTIC_MAG_1X = (RENDER_CFG.focus && RENDER_CFG.focus.detail_mag_1x) || 3;
// as a uniform (STABLE TEXT: a renderconfig value never changes the program text)
const _opticMagU = { value: Number(OPTIC_MAG_1X.toFixed(3)) };
// ---------------------------------------------------------------- quality
// Detail tier = the GROUND-M ground tier (groundmat.js): 0 low (software/CPU
// GL: the baked tile only), 1 mid (Quest and other mobile GPUs: materials on
// L0..L1, macro on L2+, short relight), 2 high (desktop GPU: L0..L2, two hex
// layers, biplanar cliffs, relight to 300 m).
// The tier is baked into the SHADER STRING, never branched via a uniform, so
// a low tier literally does not compile the extra code. Pinned by
// renderconfig.detail_quality; "auto" = device.js detectDevice: the device
// class (renderconfig.ground.tier_by_class) and the GPU probe's demotion.
const _quality = { tier: 1, gpu: '', dev: '', probe_ms: null, why: '' };
// the renderer (initDetailQuality): the ground tree's texture pool allocates
// through it (gmtex.js)
let _renderer = null;
export function initDetailQuality(renderer, force) {
  _renderer = renderer || null;
  // `force` (0|1|2) overrides config, class and probe — tools/shaderprobe.mjs
  // uses it to compile every tier's variants headlessly
  const pin = force !== undefined ? force
    : { low: 0, mid: 1, high: 2 }[RENDER_CFG.detail_quality || 'auto'];
  // classified even when the tier is pinned: telemetry reports it
  const d = detectDevice(renderer, RENDER_CFG.ground || {}, pin);
  Object.assign(_quality, { tier: d.tier, gpu: d.gpu, dev: d.cls, probe_ms: d.probe_ms, why: d.why });
  console.info(`device: ${d.cls}, probe ${d.probe_ms == null ? '—' : d.probe_ms + ' ms'}${d.probe_why ? ` (${d.probe_why})` : ''}, `
    + `terrain detail ${['low', 'mid', 'high'][d.tier]} tier (${d.why})`);
  return d.tier;
}
// shared device tier for other systems (vegetation sprite resolution etc.) —
// valid after initDetailQuality ran in boot; defaults to mid
export function detailTier() { return _quality.tier; }
// the renderer string initDetailQuality sniffed ('' when WebGL hides it)
export function detailGpu() { return _quality.gpu; }
// the device class + probe time behind the tier (device.js), for telemetry
export function detailDevice() { return { dev: _quality.dev, probe_ms: _quality.probe_ms, why: _quality.why }; }

// REAL data extent in world meters — fragments beyond it are the edge tiles'
// padded apron (flat edge-extruded heightfield with clamped-classmap paint):
// a giant fake shelf floating over the valley. Discard them; the panorama
// (real terrarium heights) owns everything beyond the core.
const _extent = { value: new THREE.Vector2(1e9, 1e9) };
// both sides: the sampler clamps negative coords to the edge row, so without
// the < 0 half a tile off the west/north edge draws an extruded fake strip
const EXT_DISCARD_GLSL = 'if (vWorldPos2.x > uExtent.x || vWorldPos2.z > uExtent.y\n'
  + '  || vWorldPos2.x < 0.0 || vWorldPos2.z < 0.0) discard;\n';


// ---------------------------------------------------------------- variants
// `discard` is not free on a tile-based deferred GPU: it forces late-Z and
// disables hidden-surface removal for the WHOLE draw call, and terrain is both
// the densest geometry and the largest fill in the frame. Three of the four
// discards terrain used to compile in unconditionally are static world facts:
//   EXT — only nodes that straddle the real data boundary can have apron
//   CUT — only nodes overlapping a station docking box
//   COR — only nodes overlapping a raster carve run (corridor.js: the decks)
//         — an emitted bore is cut into the node mesh at bake, never discarded
// A node that touches none of them (the overwhelming majority, including
// nearly every node you actually stand on) compiles a discard-free shader
// and keeps early-Z. V_GROUND / V_MACRO are the ground-material variants
// (groundmat.js) — near levels / far levels, by tier (_levelBits). V_WATER
// (WATER-G): the node meets water (groundmat waterIn: the coarse class grid
// + the 1 m tiles' extra, dilated) and compiles the water surface; without
// it the program text is the pre-WATER-G one.
// V_MORPH (GROUND-M M2, groundtree.js): a quadtree node — position carries
// the height, aD the i8 geomorph delta code (stitch.js DQ), the morph slot
// and the texture uv are derived from the position (MORPH_GLSL). Every node
// the client draws is V_MORPH; a mask without it (aH lifts a flat position)
// is the plain quad tools/shaderprobe.mjs renders the ground material on.
// THE FAR FIELD (meta.groundm.outer; groundtree.js _cls, faratlas.js):
// V_FAR — a node that is not frame-built and reaches past the frame: a
// fragment past the frame takes the far atlas (farTap: look.at's sector, the
// ring's pixels) for its colour; the atmosphere (sky.js atmos: haze past the
// frame, the scene fog over the inside part of the ray only — inside the
// frame with the eye inside it equals the plain fog) wraps the program.
// V_FARONLY — wholly past the frame: the atlas only (no pool, no ground /
// macro / water block). V_CLIP — an apron (index.json aprons): its fragments
// INSIDE uClip (the frame inset by band_m) discard. A frame-built node keeps
// V_EXT (its straddling part is extruded, never truth). The values stay, so
// a mask names the same variant in the program keys and the probe's pins.
const V_EXT = 1, V_MACRO = 2, V_CUT = 4, V_COR = 8, V_GROUND = 16, V_FAR = 32, V_WATER = 64, V_MORPH = 128,
  V_FARONLY = 256, V_CLIP = 512;
// the far program's inputs (TerrainManager: the atlas's chunk-list K; main.js setAtmos)
let _farK = 0, _atmos = null;
// the far tap's derivatives: taken before any branch (p = game xz)
const FAR_PRE_GLSL = 'vec2 farP = vWorldPos2.xz, farDx = dFdx(farP), farDy = dFdy(farP);\n';
const CLIP_GLSL = 'if (farP.x > uClip.x && farP.x < uClip.z && farP.y > uClip.y && farP.y < uClip.w) discard;\n';
const FAR_ONLY_GLSL = 'diffuseColor.rgb *= farTap(farP, farDx, farDy);\n';
const FAR_PAST_GLSL = '\nif (farP.x < 0.0 || farP.y < 0.0 || farP.x > uExtent.x || farP.y > uExtent.y) diffuseColor.rgb = diffuse * farTap(farP, farDx, farDy);\n';

// V_MORPH: node-local xz (position - uOrigin) on a border line (0.25 /
// uSide + 0.25, the L0 sample lattice) -> its slot (morph.js: 0 the node, 1..4
// top / bottom / left / right, 5..8 the corners); y = aH - (1 - slot) delta,
// delta the DQ code's (stitch.js decD: sign * q * (2^(|c| / k) - 1)). The
// border tolerance 2e-4 m: a shared vertex is one world mm on both sides, an
// off-line vertex at least 1 mm (>= 0.5 mm after float32 world rounding)
// away. invariant: two programs (a fine and a coarse node's) evaluate a
// shared vertex identically.
const MORPH_DECL_GLSL = `
invariant gl_Position;
attribute float aD;
uniform float uMorph[9];
uniform vec2 uOrigin;
uniform float uSide;
float gmSlot(vec2 l) {
  float lo = 0.25, hi = uSide + 0.25;
  bool L = abs(l.x - lo) < 2e-4, R = abs(l.x - hi) < 2e-4, T = abs(l.y - lo) < 2e-4, B = abs(l.y - hi) < 2e-4;
  int i = (L || R) && (T || B) ? 5 + (R ? 1 : 0) + (B ? 2 : 0) : T ? 1 : B ? 2 : L ? 3 : R ? 4 : 0;
  return uMorph[i];
}
float gmDelta(float c) { return sign(c) * ${DQ.q.toFixed(4)} * (exp2(abs(c) / ${DQ.k.toFixed(1)}) - 1.0); }
`;
const MORPH_UV_GLSL = `
vec2 gmL = position.xz - uOrigin;
#ifdef USE_MAP
vMapUv = (mapTransform * vec3(min(gmL.x / uSide, 1.0), 1.0 - min(gmL.y / uSide, 1.0), 1.0)).xy;
#endif
`;

// The elevation bands (meta.bands) that pick a ground layer for class 255
// (unclassified / pre-classify bakes): groundmat uBands, a uniform — no
// recompile when they change. Set once per world before the first node.
export function setDetailZones(bands) { setGroundBands(bands); }

function attachVariant(mat, mask, ring) {
  const wantGround = (mask & V_GROUND) !== 0;
  const wantMacro = !wantGround && (mask & V_MACRO) !== 0;
  const wantDetail = wantGround || wantMacro;
  // WATER-G: every V_GROUND program carries the water term (a uniform
  // branch on the class weights it already has, off until applyWater);
  // V_MACRO / bare take it by V_WATER (their water needs its own tap)
  const wantWater = wantGround || (mask & V_WATER) !== 0;
  const wantMorph = (mask & V_MORPH) !== 0 && ring && ring.gm;
  // the far field (V_FAR / V_FARONLY / V_CLIP): the atlas past the frame
  const wantFar = !!(wantMorph && (mask & V_FAR) && ring.gm.uFarAtlas), farOnly = wantFar && (mask & V_FARONLY) !== 0;
  // GROUND-M M2b: the node's colour from the virtual texture pool (gmtex.js); none wholly past the frame
  const wantVt = !!(wantMorph && ring.gm.uVtIdx) && !farOnly;
  // S2 rough terms (tier 2): every ground tree node (error-driven: a planar
  // patch stays coarse inside the optic's reach, so any node can be inside
  // rough_end_m, optic-scaled); the probe's plain quad at level 0
  const wantRough = wantGround && (wantMorph || !ring || ring.level === 0);
  const patch = (shader) => {
    if (wantMorph) Object.assign(shader.uniforms, ring.gm);
    shader.uniforms.uExtent = _extent;
    if (mask & V_CUT) cutUniforms(shader);
    if (mask & V_COR) corridorUniforms(shader);
    if (wantDetail) {
      shader.uniforms.uWedge = _wedge;
      shader.uniforms.uZoom = _zoomU;
      shader.uniforms.uOpticMag = _opticMagU;
      bindGround(shader.uniforms);
    } else if (wantWater) {
      // WATER-G: the water surface on every tier — a variant without a
      // ground block (tier 0) still draws it (groundmat bareWaterGLSL)
      bindGround(shader.uniforms);
    }
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>',
        '#include <common>\n' + (wantMorph ? MORPH_DECL_GLSL : 'attribute float aH;') + '\nvarying vec3 vWorldPos2;'
        + (wantDetail ? '\nvarying float vViewDist;' : ''))
      // V_MORPH: the position carries the height, less the morph slot's share
      // of the delta; the plain quad's position carries y = 0 and the height
      // rides in aH, lifted here so project_vertex and every later varying
      // see it.
      .replace('#include <uv_vertex>', wantMorph ? MORPH_UV_GLSL : '#include <uv_vertex>')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\n' + (wantMorph
        ? 'transformed.y = position.y - (1.0 - gmSlot(position.xz - uOrigin)) * gmDelta(aD);'
        : 'transformed.y = aH;'))
      .replace('#include <fog_vertex>',
        // NOTE: three only declares `worldPosition` under envmap/shadow/etc.
        // defines — never for our plain basic materials. Referencing it kills
        // the whole program (mesh silently vanishes). Compute it ourselves.
        '#include <fog_vertex>\nvWorldPos2 = (modelMatrix * vec4(transformed, 1.0)).xyz;'
        + (wantDetail ? '\nvViewDist = -mvPosition.z;' : ''));

    let decl = '#include <common>\nuniform vec2 uExtent;\nvarying vec3 vWorldPos2;\n';
    if (mask & V_CUT) decl += CUT_DECL_GLSL;
    if (mask & V_COR) decl += COR_DECL_GLSL;
    if (wantVt) decl += VT_DECL_GLSL;
    if (wantFar) decl += farDecl(_farK) + (mask & V_CLIP ? 'uniform vec4 uClip;\n' : '');
    const q = _quality.tier;
    if (wantDetail) {
      decl += `
        uniform vec4 uWedge;
        uniform float uZoom;
        uniform float uOpticMag;
        varying float vViewDist;` + groundDecl(q, wantWater, { use: wantGround ? 'ground' : 'macro', rough: wantRough });
    } else if (wantWater) {
      decl += waterDecl();
    }

    let pre = (wantVt ? VT_PRE_GLSL : '') + (wantFar ? FAR_PRE_GLSL : '');
    if (wantFar && (mask & V_CLIP)) pre += CLIP_GLSL;
    if (mask & V_EXT) pre += EXT_DISCARD_GLSL;
    if (mask & V_CUT) pre += cutGlsl('vWorldPos2');
    if (mask & V_COR) pre += corGlsl('vWorldPos2');

    // GROUND-M (groundmat.js): every derivative inside is taken before any
    // branch and every material tap is a textureGrad — no implicit-derivative
    // sampling in divergent control flow (the old tier-2 nondeterminism)
    const post = wantDetail ? `
        {
          // the fades are ANGULAR: in an optic pass (uWedge.w = 1) a view
          // magnified uZoom x uOpticMag (focus.detail_mag_1x) reaches them that
          // much further out — invented texture where the ortho is magnified, never geometry.
          float vdD = vViewDist / mix(1.0, max(1.0, uZoom * uOpticMag), uWedge.w);
          ${wantGround ? groundGLSL(q, wantWater, wantVt ? VT_MAP : undefined, wantRough) : macroGLSL(wantWater)}
        }` : wantWater ? bareWaterGLSL() : '';

    // ROCK-F C: V_GROUND's colour tap follows the slope (groundmat mapGLSL)
    const mapG = farOnly ? FAR_ONLY_GLSL : wantVt ? (wantGround && mapGLSL(q, VT_MAP)) || vrsGroundMap(VT_MAP_GLSL)
      : (wantGround && mapGLSL(q)) || '#include <map_fragment>';
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', decl)
      .replace('#include <map_fragment>', pre + mapG + post + (wantFar && !farOnly ? FAR_PAST_GLSL : ''));
    shader.vertexShader = glslMin(shader.vertexShader);
    shader.fragmentShader = glslMin(shader.fragmentShader);
  };
  // three caches programs by material; make each variant distinct. The key
  // must cover EVERYTHING string-interpolated into the GLSL: the quality tier
  // and groundmat's switches / layer map (groundKey; its numbers are
  // uniforms), the far tap's K — a mask-only key silently reuses a stale
  // program. V_GROUND ignores V_WATER (one program either way).
  const key = () => `terrain-v${wantGround ? mask & ~V_WATER : mask}-q${_quality.tier}-m${wantMorph ? 1 : 0}${wantVt ? '-t1' : ''}`
    + (wantDetail ? `-${groundKey()}` : '') + (wantGround ? `-r${wantRough ? 1 : 0}` : '') + (wantWater ? `-${waterKey()}` : '')
    + (wantFar ? `-f${_farK}${_atmos ? 'a' : ''}` : '') + (wantVt && !wantGround ? vrsGroundKey() : '');
  // the far field: the atmosphere wraps the program (it calls patch after its
  // own replacements: the morph lands before its vHazeW)
  if (wantFar && _atmos) _atmos(mat, null, patch, 'far');
  else mat.onBeforeCompile = patch;
  mat.customProgramCacheKey = key;
}

export class TerrainManager {
  constructor(scene, meta, baseUrl) {
    if (!meta.groundm) throw new Error('world meta.groundm missing: the ground tree is required (the GROUND-M steps)');
    this.scene = scene;
    this.meta = meta;
    this.baseUrl = baseUrl;
    this.group = new THREE.Group();
    this.group.name = 'terrain';
    this.group.matrixAutoUpdate = false;   // static: the nodes' world matrices are set once (groundtree.js _show)
    scene.add(this.group);
    // bumped whenever the drawn node set changes (groundtree.js _show):
    // groundDrawn readers (the baker-2 structure far rule, structs2.js)
    // re-sample on a change
    this.gen = 0;
    // pixel angle sources (main.js viewPixelAngle / basePixelAngle /
    // opticPixelAngle): the tree's screen error and the pool's fill
    this.pixelAngle = null;
    // texture rev 3 tiles store radiance / W (bake.py light_spec): the
    // material colour scales them back to the light model's radiance, the
    // scale the renderer's one tone curve expects (look.js setupLook).
    this.lightGain = meta.tex.light.white;
    if (!(this.lightGain > 0)) throw new Error('world meta.tex.light.white missing: texture rev 3 required');
    // REAL data extent (V_EXT, the keepers' node grid)
    const bb = meta.bbox;
    this.extentX = bb.e1 - bb.e0;
    this.extentZ = bb.n1 - bb.n0;
    // the renderconfig rings over the bake's levels (game/lodmath.js
    // buildRings): each level's radius_m is the texture pool's floor reach
    // (gmtex.js), its level the material's tier bits (_levelBits)
    this.rings = buildRings(RENDER_CFG.rings, meta.levels, this.extentX, this.extentZ);
    this.carveGen = -1;           // last cut/corridor/water registration we classified against
    this._waterOn = false;        // WATER-G: V_WATER decided (applyWater)
    setWaterOn(false);            // (a new world: V_GROUND's water term off until then)
    // NEAR READY (renderconfig load.near_levels): the ground the player
    // stands on is drawn (groundtree.js update -> _setNear). main.js's
    // beam-in latch holds on it (never on `pending === 0`, which also waits
    // for the far levels). False until the tree's first near set — before
    // entry, until a target (a card's prefetch, a GPS fix) is set.
    this._nearLevels = (RENDER_CFG.load && RENDER_CFG.load.near_levels) ?? 2;
    this._near = false;
    this._armNear();
    this.firstTileAt = 0;         // performance.now() of the first node shown (load report)
    _extent.value.set(this.extentX, this.extentZ);
    // THE FAR ATLAS (faratlas.js): one per world with a far tree (meta.groundm.outer),
    // sized by renderconfig vt.far; ring.js's gen pass paints it (main.js hands it over)
    this.farAtlas = null;
    if (meta.groundm.outer) {
      const px = farAtlasPx(RENDER_CFG.vt && RENDER_CFG.vt.far, _quality.tier, _quality.dev,
        _renderer ? _renderer.capabilities.maxTextureSize : Infinity);
      if (!px) throw new Error('renderconfig vt.far missing: a far tree needs the far atlas');
      if (!(meta.panorama && meta.panorama.classes)) throw new Error('world meta.panorama.classes missing: the far atlas needs the sector look');
      this.farAtlas = createFarAtlas(() => _renderer, { w: px[0], h: px[1], classes: meta.panorama.classes });
      _farK = this.farAtlas.K;
    }
    // GROUND-M (groundtree.js, APP_GROUNDM.md): the quadtree draws the
    // ground; its colour is the virtual texture pool (gmtex.js, M2b)
    const vt = new GmTex(this, meta, { tier: _quality.tier, getRenderer: () => _renderer,
      kpx: RING_TIERS.texel_px ?? 1, aniso: RENDER_CFG.anisotropy || 8 });
    this.gm = new GroundTree(this, meta.groundm, vt);
    // GENSURF: the device paints the pool's slots (groundgen.js, the 'ground' GenPass)
    this.ground = new GroundGen(this, meta, vt, () => _renderer);
  }

  // the tree's queued + in-flight node loads: the "still streaming" figure.
  // The beam-in latch never waits on it (nearReady below).
  get pending() { return this.gm.pending; }

  // The near ground is drawn (see the ctor). `nearReady` is the state now;
  // whenNearReady() resolves when it next is (at once while it is). A jump
  // that leaves the near set unmet re-arms it with a fresh promise; an
  // unresolved promise is never replaced, so a waiter always waits for the
  // CURRENT near set.
  get nearReady() { return this._near; }
  whenNearReady() { return this._nearP; }

  _armNear() { this._nearP = new Promise((res) => { this._nearRes = res; }); }

  // the ground tree's near state (groundtree.js update)
  _setNear(ok) {
    if (ok && !this._near) { this._near = true; this._nearRes(); }
    else if (!ok && this._near) { this._near = false; this._armNear(); }
  }

  // Feed the bake's surface classmap (a loadSurface result) to the ground
  // materials: the raw class ids of the shipped coarse grid (meta.surface.
  // coarse, made by pipeline/bake.py classmap_coarse_stride; field() serves
  // only that stride), which groundmat maps class -> layer in the shader.
  // Data-driven through a shared uniform — no recompiles, resident nodes pick
  // the classes up on their next draw.
  setSurfaceClasses(surf) {
    if (!surf || !surf.field) return;
    const stride = surf.meta.coarse && surf.meta.coarse.stride;
    if (!(stride > 0)) throw new Error('world meta.surface.coarse.stride missing: the coarse class grid is required');
    setCoarseClasses(surf.field(stride));
    if (this.ground) this.ground.setSurface(surf);
  }

  // the mask bits a level alone decides (every node of the level)
  _levelBits(ring) {
    // ground materials (groundmat.js): the full shader on L0..L1 (tier 1) /
    // L0..L2 (tier 2 — its near fade reaches ~450 m), the macro tap on every
    // level above; tier 0 none
    const q = _quality.tier;
    if (vrsGroundOn()) return 0;   // THE VR LOOK: the pool tap alone (vrstyle/ground.js)
    return q < 1 ? 0 : ring.level <= (q >= 2 ? 2 : 1) ? V_GROUND : V_MACRO;
  }

  // Static shader-variant mask from a world AABB (ring-like: level, tile_m).
  // Called once per node, and again whenever a carve source registers (the
  // funicular and the station zones both land after the first nodes have).
  _maskFor(ring, tx, ty, y0, y1) {
    const tileM = ring.tile_m;
    const x0 = tx * tileM, x1 = x0 + tileM;
    const z0 = ty * tileM, z1 = z0 + tileM;
    let mask = this._levelBits(ring);
    // apron only exists where the padded grid runs past the real data window
    if (x1 > this.extentX || z1 > this.extentZ) mask |= V_EXT;
    const hit = (b) => b[0] < x1 && b[1] > x0 && b[2] < z1 && b[3] > z0
      && b[4] < y1 && b[5] > y0;
    for (const b of cutBoxes()) { if (hit(b)) { mask |= V_CUT; break; } }
    for (const b of corridorBoxes()) { if (hit(b)) { mask |= V_COR; break; } }
    // WATER-G: xz only (conservative), once the class grid's variants are
    // compiled (applyWater); never V_GROUND (its programs all carry water)
    if (this._waterOn && !(mask & V_GROUND) && waterIn(x0, z0, x1, z1)) mask |= V_WATER;
    return mask;
  }

  // a ground tree node's mask (groundtree.js): the level rules one level
  // down — a node is drawn about half as far out as its level's ring reaches
  // (L1 to ~200 m, L2 to ~400 m vs the rings' 512 / 1536 m), so V_GROUND
  // keeps the rings' reach (tier 1: L0..L2, tier 2: L0..L3; the near fade
  // ~450 m)
  // The far field (groundtree.js _cls): an apron V_FAR | V_FARONLY | V_CLIP;
  // a node wholly past the frame V_FAR | V_FARONLY (no level bits); a
  // straddler (an outer node, k > outer.first) its level's bits less V_EXT,
  // plus V_FAR; a frame-built node as before.
  _gmMask(ring, tx, ty, y0, y1) {
    const O = this.meta.groundm.outer;
    if (O && ring.apron) return V_MORPH | V_FAR | V_FARONLY | V_CLIP;
    if (O) {
      const s = ring.tile_m;
      if (tx * s >= this.extentX || ty * s >= this.extentZ || (tx + 1) * s <= 0 || (ty + 1) * s <= 0) return V_MORPH | V_FAR | V_FARONLY;
      if (ring.level > O.first) return (this._maskFor({ ...ring, level: ring.level - 1 }, tx, ty, y0, y1) & ~V_EXT) | V_MORPH | V_FAR;
    }
    return this._maskFor({ ...ring, level: Math.max(0, ring.level - 1) }, tx, ty, y0, y1) | V_MORPH;
  }

  // the far field's atmosphere (main.js: sky.js atmosFor), before the first node
  setAtmos(atmos) { _atmos = atmos || null; }

  // WATER-G: the class grid landed (setSurfaceClasses) and main.js has
  // compiled the water variants variantKeepers returned (warmWater): resident
  // V_MACRO / bare nodes that meet water swap to them, every node made from
  // now on asks; V_GROUND's water term turns on (uWaterOn) in the same frame.
  applyWater() {
    if (this._waterOn) return;
    this._waterOn = true;
    setWaterOn(true);
    this._reclassify();
  }

  _material(tile, mask) {
    const mat = new THREE.MeshBasicMaterial({ map: tile.tex, fog: true });
    mat.color.setScalar(this.lightGain);    // linear, no colour-space convert
    attachVariant(mat, mask, tile.ring);
    return mat;
  }

  // Shader warm-up (main.js warmShaders, warmWater): one keeper mesh per
  // V_MORPH mask a node of this world can take — each level's bits (one
  // level down, _gmMask) × every EXT/CUT/COR/WATER combination its node grid
  // meets (xz overlap only: a superset of _maskFor's y-aware test) — with
  // the material a node builds (the keeper's uniforms are stand-ins; the
  // program is what is kept). They never join the scene and are never
  // disposed: compiling them builds the variants before play, and holding
  // them (main.js pins each group in its compile queue: compileq.js pin, the
  // one registry) keeps those programs alive when the last node of a variant
  // is evicted (three frees a program with its last material, and streaming
  // used to rebuild the same variant mid-game).
  // Returns a group of the keepers not handed out before (null: none new).
  variantKeepers() {
    if (!this._kept) {
      this._kept = new Set();       // masks with a keeper (the keepers: the caller pins them)
      this._keepGeo = new THREE.BufferGeometry();
      this._keepGeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(9), 3));
      this._keepGeo.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(6), 2));
    }
    const cuts = cutBoxes(), cors = corridorBoxes();
    const meets = (list, x0, x1, z0, z1) => {
      for (const b of list) if (b[0] < x1 && b[1] > x0 && b[2] < z1 && b[3] > z0) return true;
      return false;
    };
    const out = new THREE.Group();
    const O = this.meta.groundm.outer;
    const keep = (mask, ring) => {
      if (this._kept.has(mask)) return;
      this._kept.add(mask);
      // a pool-coloured node has no map (gmtex.js): neither has its keeper
      const keeper = new THREE.Mesh(this._keepGeo, this._material({ tex: null, ring }, mask));
      keeper.matrixAutoUpdate = false;
      out.add(keeper);
    };
    for (const ring of this.gm.keeperRings(this.extentX, this.extentZ)) {
      // the far field: a straddler's level (k > outer.first) takes V_FAR, never V_EXT;
      // the beyond and the apron programs once (_gmMask)
      const far = !!(O && ring.level > O.first);
      if (O && ring.level === this.gm.top) { keep(V_MORPH | V_FAR | V_FARONLY, ring); keep(V_MORPH | V_FAR | V_FARONLY | V_CLIP, { ...ring, apron: true }); }
      const base = this._levelBits({ ...ring, level: Math.max(0, ring.level - 1) }) | V_MORPH | (far ? V_FAR : 0);
      const flags = new Set();
      const t = ring.tile_m;
      for (let ty = 0; ty < ring.nyReal; ty++) {
        for (let tx = 0; tx < ring.nxReal; tx++) {
          const x0 = tx * t, z0 = ty * t;
          let f = !far && (x0 + t > this.extentX || z0 + t > this.extentZ) ? V_EXT : 0;
          if (meets(cuts, x0, x0 + t, z0, z0 + t)) f |= V_CUT;
          if (meets(cors, x0, x0 + t, z0, z0 + t)) f |= V_COR;
          // WATER-G: V_GROUND programs all carry the water term
          if (!(base & V_GROUND) && waterIn(x0, z0, x0 + t, z0 + t)) f |= V_WATER;
          flags.add(f);
        }
      }
      for (const f of flags) keep(base | f, ring);
    }
    return out.children.length ? out : null;
  }

  // Re-classify the nodes after a carve source registered (groundtree.js
  // reclassify: only a node whose mask changed swaps its material).
  _reclassify() { this.gm.reclassify(); }

  update(pos) {
    const gen = cutGen() + corridorGen() + waterGen();
    if (gen !== this.carveGen) { this.carveGen = gen; sec('terrain.reclassify'); this._reclassify(); sec('terrain'); }
    this.gm.update(pos, performance.now());
  }

  // The ground drawn at (x, z): the ground tree's node meshes, one surface
  // for the naked eye and the optic (NaN where no node covers the point).
  // Readers: the structure far rule (structs2.js / structplan.js FAR_RULE),
  // the trail lines (traillines.js), the crowns / shrubs (yfrom.js: at.k =
  // the level read; drawnLevel = the finest drawn level there, -1 none).
  groundDrawn(x, z, at = null) { return this.gm.groundDrawn(x, z, at); }
  drawnLevel(x, z) { return this.gm.drawnLevel(x, z); }

  // the optic draws the naked eye's nodes (no wedge); the pool streams the
  // focus tiles for it (gmtex.js, pool 1)
  updateFocus(focus) { this.gm.focus = focus || null; }
}
